import { sportHaveDraw } from 'api/mockSportCategory';
import { useAppTranslation } from 'app/hooks';
import { MAIN_MARKETS_LIST } from 'consts/app';
import { GameResponseModel, OddModel } from 'models';
import React, { Fragment, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { makeSelectGameOddsById } from 'redux/gameSlice';
import { validateOdd } from 'utils/betUtils';
import { checkMoreSportActive } from 'utils/commonUtils';
import BetButton from './BetButton';
import WagerItem from './common/WagerItem';
import InactiveButton from './InactiveButton';

interface OddsListProps {
  gameId: string;
  isMoreSportsActive?: boolean;
  category: string;
  game: GameResponseModel;
  renderWaggerSoccer?: boolean;
}

const OddsList: React.FunctionComponent<OddsListProps> = ({
  gameId,
  isMoreSportsActive = false,
  category,
  game,
  renderWaggerSoccer = false,
}) => {
  const selectGameOddsById = useMemo(() => makeSelectGameOddsById(game.id), [gameId]);
  const oddsByGameId = useSelector(selectGameOddsById);

  if (renderWaggerSoccer) {
    return <RenderSoccerWagger game={game} category={category} gameOddsById={oddsByGameId} />;
  }

  return (
    <>
      {checkMoreSportActive(category) && category !== 'cricket' ? (
        <RenderMoreSportOdds game={game} category={category} gameOddsById={oddsByGameId} />
      ) : (
        <div className={`match-item__right main`}>
          {!sportHaveDraw.includes(category) ? (
            <RenderNormalSport game={game} category={category} gameOddsById={oddsByGameId} />
          ) : (
            <Fragment>
              <RenderSportDraw game={game} category={category} gameOddsById={oddsByGameId} />
            </Fragment>
          )}
        </div>
      )}
    </>
  );
};

export default OddsList;

interface RenderOddsProps {
  game: GameResponseModel;
  category: string;
  gameOddsById: {
    [marketTypeId: string]: OddModel[];
  };
}

const RenderMoreSportOdds: React.FunctionComponent<RenderOddsProps> = ({
  game,
  category,
  gameOddsById,
}) => {
  const oddsMoneyLine =
    (
      gameOddsById['money_line'] ||
      gameOddsById['match_result'] ||
      gameOddsById['fight_result_draw_no_bet_']
    )?.filter((odd) => validateOdd(odd)) || [];

  return (
    <div className="match-item__right single">
      <div className={`group-buttons`}>
        {!oddsMoneyLine ||
        oddsMoneyLine.length < 0 ||
        (category === 'tennis' && game.status === 'LIVE') ? (
          <div key={`${game.id}&&${'money_line'}`} className={`group-buttons`}>
            <InactiveButton />
            <InactiveButton />
          </div>
        ) : (
          <div key={`${game.id}&&${'money_line'}`} className={`group-buttons`}>
            {oddsMoneyLine.length === 2 ? (
              [...oddsMoneyLine]
                .sort((x, y) =>
                  x.selectionLine === 'Player 1' ||
                  x.selection === game.homeTeam ||
                  x.selectionLine?.toLowerCase() === 'under'
                    ? 1
                    : -1,
                )
                .map((odd, index) => {
                  return (
                    <>
                      <BetButton
                        key={`${odd.id}`}
                        odd={odd}
                        index={index}
                        gameId={game.id}
                        homeTeam={game.homeTeam}
                        awayTeam={game.awayTeam}
                        category={category}
                        // isShowAnimation={status[odd.id]}
                        homeTeamLogo={game.homeTeamLogo}
                        awayTeamLogo={game.awayTeamLogo}
                        gameStatus={game.status}
                      />
                    </>
                  );
                })
            ) : (
              <Fragment>
                <InactiveButton />
                <InactiveButton />
              </Fragment>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const RenderSportDraw: React.FunctionComponent<RenderOddsProps> = ({
  game,
  category,
  gameOddsById,
}) => {
  const odds =
    (
      gameOddsById['money_line'] ||
      gameOddsById['match_result'] ||
      gameOddsById['match_winner']
    )?.filter((odd) => validateOdd(odd)) || [];

  return odds.length >= 2 ? (
    <Fragment>
      {[game.homeTeam, 'Draw', game.awayTeam].map((item, index) => {
        const odd = odds.find((o) => o.selection === item);
        const isValid = odd;
        return (
          <div className="group-buttons center" key={`${game.id}&&${item}`}>
            {isValid ? (
              <BetButton
                odd={odd}
                index={index}
                gameId={game.id}
                homeTeam={game.homeTeam}
                awayTeam={game.awayTeam}
                category={category}
                homeTeamLogo={game.homeTeamLogo}
                awayTeamLogo={game.awayTeamLogo}
                gameStatus={game.status}
              />
            ) : (
              <InactiveButton />
            )}
          </div>
        );
      })}
    </Fragment>
  ) : (
    <Fragment>
      <div className="group-buttons center">
        <InactiveButton />
      </div>
      <div className="group-buttons center">
        <InactiveButton />
      </div>
      <div className="group-buttons center">
        <InactiveButton />
      </div>
    </Fragment>
  );
};

export const RenderNormalSport: React.FunctionComponent<RenderOddsProps> = ({
  game,
  category,
  gameOddsById,
}) => {
  return (
    <Fragment>
      {MAIN_MARKETS_LIST.map((marketGroup) => {
        const groupOdds = Object.values(gameOddsById || {})
          .flat()
          .filter((odd) => marketGroup.list.includes(odd.market) && validateOdd(odd));
        const sortedOdds = groupOdds.sort((a, b) => {
          const aPriority =
            a.selection === game.homeTeam || a.selectionLine?.toLowerCase() === 'under' ? 1 : -1;
          const bPriority =
            b.selection === game.homeTeam || b.selectionLine?.toLowerCase() === 'under' ? 1 : -1;
          return aPriority - bPriority;
        });

        return (
          <div key={`${game.id}&&${marketGroup.id}`} className="group-buttons">
            {sortedOdds.length === 2 ? (
              sortedOdds.map((odd, index) => (
                <BetButton
                  key={odd.id}
                  odd={odd}
                  index={index}
                  gameId={game.id}
                  homeTeam={game.homeTeam}
                  awayTeam={game.awayTeam}
                  category={category}
                  homeTeamLogo={game.homeTeamLogo}
                  awayTeamLogo={game.awayTeamLogo}
                  gameStatus={game.status}
                />
              ))
            ) : (
              <>
                <InactiveButton />
                <InactiveButton />
              </>
            )}
          </div>
        );
      })}
    </Fragment>
  );
};
export const RenderSoccerWagger: React.FC<RenderOddsProps> = ({ game, category, gameOddsById }) => {
  const t = useAppTranslation();

  return (
    <Fragment>
      {['odd_or_even_total', 'half_time_both_teams_to_score', 'total_goals_over_under'].map(
        (market) => {
          const odds = gameOddsById[market]?.filter((odd) => validateOdd(odd)) || [];
          const sortedOdds =
            odds.length >= 2
              ? [...odds].sort((a, b) => (a.selection?.toLowerCase() === 'under' ? 1 : -1))
              : [];

          if (odds.length < 2) return null;
          const marketName = odds[0]?.marketName ?? '';
          return (
            <WagerItem
              title={`${marketName}`}
              game={game}
              category={category}
              odds={sortedOdds}
              type="alternate"
            />
          );
        },
      )}
    </Fragment>
  );
};
